/**
 * Client for the recent-practice-sessions endpoints powering the "Recent
 * Practice Sessions" section on `/history` and the deep-link review page at
 * `/history/session/:sessionId`.
 *
 *   GET /practice/sessions                                → list
 *   GET /practice/session/adaptive/:sessionId/recap       → recap
 */
import type { RevisionSessionProjection } from '@shared/revisionSession';

import type {
  AdaptiveSessionMeta,
  AdaptiveSessionReviewAttempt,
  AdaptiveSessionSummary,
  DiagnosticPlacement,
} from './adaptivePracticeApi';
import { fetchWithConfig } from './apiUtils';

export interface RecentSessionListItem {
  sessionId: string;
  sessionMode: 'drill' | 'exam';
  subject: string | null;
  topic: string | null;
  /**
   * Syllabus topic names for the nodes actually practiced, most-practiced
   * first. Drives the row label; empty when none resolve (falls back to
   * topic/subject). See SessionRow.sessionLabel.
   */
  topics: string[];
  startedAt: string;
  endedAt: string | null;
  status: 'ended' | 'expired';
  durationMs: number | null;
  totalAttempts: number;
  assessedAttempts?: number;
  correctCount: number;
  /** Null when totalAttempts === 0. */
  accuracy: number | null;
  /**
   * True when this row was the cold-start placement diagnostic — the whole
   * sitting is ONE row, labeled "Initial diagnostic" by SessionRow. Optional
   * because rows serialized before this field shipped omit it.
   */
  isDiagnostic?: boolean;
  /** Present iff the row was a revision round (level-free projection). */
  revision?: RevisionSessionProjection;
}

export interface RecentSessionsResponse {
  items: RecentSessionListItem[];
  nextCursor: string | null;
}

export interface RecentSessionsParams {
  mathSubject?: 'E-Math' | 'A-Math';
  limit?: number;
  cursor?: string;
  /** Staff viewing a linked student's sessions. Omit for self-view. */
  userId?: string | null;
}

/**
 * Read-only mirror of the `/session/adaptive/:id/end` envelope — same
 * summary + attempts fields, minus `streak` (already bumped at end).
 */
export interface SessionRecapResponse {
  session: AdaptiveSessionMeta;
  summary: AdaptiveSessionSummary;
  attempts: AdaptiveSessionReviewAttempt[];
  truncated: boolean;
  /**
   * Frozen placement profile — present iff the session was minted as the
   * cold-start diagnostic. The review page leads with it (§"here's where we
   * started you") before the per-attempt cards.
   */
  placement?: DiagnosticPlacement;
}

export interface GetRecapParams {
  userId?: string | null;
}

export const practiceSessionsApi = {
  async list(
    params: RecentSessionsParams | undefined,
    token: string
  ): Promise<RecentSessionsResponse> {
    const query = new URLSearchParams();
    if (params?.mathSubject) query.set('mathSubject', params.mathSubject);
    if (params?.limit) query.set('limit', String(params.limit));
    if (params?.cursor) query.set('cursor', params.cursor);
    if (params?.userId) query.set('userId', params.userId);
    const qs = query.toString();
    return fetchWithConfig<RecentSessionsResponse>(
      `/practice/sessions${qs ? `?${qs}` : ''}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },

  async getRecap(
    sessionId: string,
    params: GetRecapParams | undefined,
    token: string
  ): Promise<SessionRecapResponse> {
    const query = new URLSearchParams();
    if (params?.userId) query.set('userId', params.userId);
    const qs = query.toString();
    return fetchWithConfig<SessionRecapResponse>(
      `/practice/session/adaptive/${encodeURIComponent(sessionId)}/recap${qs ? `?${qs}` : ''}`,
      {
        method: 'GET',
        authToken: token,
      }
    );
  },
};
